import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import { HubConnectionState } from "@microsoft/signalr";
import { useDocumentService, FolderExportJobResponse } from "../../services/documentService";
import { useSnackbar } from "../../contexts/snackbar-context";
import { useFolderExportSignalR } from "../../hooks/useFolderExportSignalR";
import {
  getExportDownloadStartMessage,
  mergeExportJobDownloads,
  EXPORT_DOWNLOAD_HELP_TEXT,
  triggerExportJobDownloads,
} from "../../utils/folderExportDownloads";

type ExportStatus = "Queued" | "Running" | "Completed" | "Failed";

const statusLabel = (status: number): ExportStatus => {
  if (status === 0) {
    return "Queued";
  }

  if (status === 1) {
    return "Running";
  }

  if (status === 2) {
    return "Completed";
  }

  return "Failed";
};

const statusColor = (status: number): "default" | "success" | "error" | "warning" | "info" => {
  if (status === 2) {
    return "success";
  }

  if (status === 3) {
    return "error";
  }

  if (status === 1) {
    return "warning";
  }

  return "info";
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatBytes = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const getExportTargetLabel = (job: FolderExportJobResponse): string => {
  const explicit = job.exportLabel?.trim();
  if (explicit) {
    return explicit;
  }

  const fileName = job.fileName?.trim();
  if (fileName) {
    return fileName;
  }

  return "Export";
};

const getJobReason = (job: FolderExportJobResponse): string => {
  if (job.status === 3) {
    return job.errorMessage?.trim() || "Export failed.";
  }

  if (job.status === 0) {
    return "Queued: waiting for the export worker to pick this up. It will start automatically. If it stays stuck, the system marks it Failed so you can retry.";
  }

  if (job.status === 1) {
    return "Export is currently being processed.";
  }

  return "Export completed successfully.";
};

const DocumentExports: React.FC = () => {
  const { listFolderExportJobs, getFolderExportJob, logFolderExportDownload, retryFolderExportJob } = useDocumentService();
  const { showError, showInfo, showSuccess } = useSnackbar();
  const listJobsRef = useRef(listFolderExportJobs);

  const [jobs, setJobs] = useState<FolderExportJobResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "queued" | "running" | "completed" | "failed">("all");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  useEffect(() => {
    listJobsRef.current = listFolderExportJobs;
  }, [listFolderExportJobs]);

  const upsertJob = useCallback((incoming: FolderExportJobResponse) => {
    setJobs((previous) => {
      const index = previous.findIndex((x) => x.jobId === incoming.jobId);
      const next = [...previous];

      if (index >= 0) {
        next[index] = mergeExportJobDownloads(previous[index], incoming);
      } else {
        next.unshift(incoming);
      }

      next.sort((a, b) => {
        const aTicks = new Date(a.createdAtUtc || 0).getTime();
        const bTicks = new Date(b.createdAtUtc || 0).getTime();
        return bTicks - aTicks;
      });

      return next;
    });
  }, []);

  const refreshJobs = useCallback(async (showSpinner = false) => {
    if (showSpinner) {
      setIsLoading(true);
    }

    try {
      const result = await listJobsRef.current(100);
      setJobs(result);
    } catch (error) {
      showError(`Failed to load export jobs: ${(error as Error).message}`);
    } finally {
      if (showSpinner) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [showError]);

  const handleConnectionError = useCallback((message: string) => {
    showError(message);
  }, [showError]);

  const { isConnected, connectionState } = useFolderExportSignalR({
    onJobUpdated: upsertJob,
    onConnectionError: handleConnectionError,
  });

  useEffect(() => {
    // Initial load with spinner.
    refreshJobs(true);
  }, [refreshJobs]);

  useEffect(() => {
    // Safety net polling:
    // - SignalR is primary.
    // - While connected, keep only a slow guard poll.
    // - While disconnected/reconnecting, use faster fallback polling.
    const hasActiveJobs = jobs.some((x) => x.status === 0 || x.status === 1);
    const intervalMs = isConnected
      ? (hasActiveJobs ? 30000 : 0)
      : (hasActiveJobs ? 15000 : 45000);

    if (!intervalMs) {
      return;
    }

    const fallbackPollId = window.setInterval(() => {
      refreshJobs(false);
    }, intervalMs);

    return () => {
      window.clearInterval(fallbackPollId);
    };
  }, [isConnected, jobs, refreshJobs]);

  useEffect(() => {
    // Pull once on reconnect to recover any updates missed during reconnect windows.
    if (isConnected) {
      refreshJobs(false);
    }
  }, [isConnected, refreshJobs]);

  const inProgressCount = useMemo(
    () => jobs.filter((x) => x.status === 1).length,
    [jobs]
  );

  const queuedCount = useMemo(
    () => jobs.filter((x) => x.status === 0).length,
    [jobs]
  );

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesStatus =
        statusFilter === "all"
        || (statusFilter === "queued" && job.status === 0)
        || (statusFilter === "running" && job.status === 1)
        || (statusFilter === "completed" && job.status === 2)
        || (statusFilter === "failed" && job.status === 3);

      return matchesStatus;
    });
  }, [jobs, statusFilter]);

  const pagedJobs = useMemo(() => {
    const start = page * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredJobs.length / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredJobs, page, pageSize]);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            Export Jobs
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Track folder export status and download completed archives.
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block", mt: 0.5, maxWidth: 720 }}>
            {EXPORT_DOWNLOAD_HELP_TEXT}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
            In progress: {inProgressCount}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
            Queued: {queuedCount}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
            Realtime: {isConnected ? "Connected" : connectionState === HubConnectionState.Reconnecting ? "Reconnecting" : "Disconnected (fallback refresh active)"}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refreshJobs(true)}
          disabled={isLoading}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            borderColor: "hsl(19 101% 51% / 1)",
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ backgroundColor: "#111", border: "1px solid rgba(255,255,255,0.2)" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid rgba(255,255,255,0.14)" }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <InputLabel sx={{ color: "rgba(255,255,255,0.8)" }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as "all" | "queued" | "running" | "completed" | "failed")}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="queued">Queued</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {hasLoadedOnce && isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.45)",
                borderRadius: 1,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Refreshing...
                </Typography>
              </Stack>
            </Box>
          )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#fff", fontWeight: 700, maxWidth: 260, width: 260 }}>Path</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Progress</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Processed</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Created</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Completed</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasLoadedOnce && isLoading ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", py: 3 }}>
                  <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
                    <CircularProgress size={18} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Loading export jobs...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : filteredJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", py: 3 }}>
                  No export jobs match your filters.
                </TableCell>
              </TableRow>
            ) : (
              pagedJobs.map((job) => {
                const canDownload = job.status === 2;
                const canRetry = job.status === 3;
                const canShowReason = job.status === 0 || job.status === 1 || job.status === 3;

                return (
                  <TableRow key={job.jobId} hover>
                    <TableCell sx={{ color: "#fff", maxWidth: 260, width: 260 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          lineHeight: 1.25,
                        }}
                        title={getExportTargetLabel(job)}
                      >
                        {getExportTargetLabel(job)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>
                      <Chip
                        size="small"
                        label={statusLabel(job.status)}
                        color={statusColor(job.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>{job.progressPercent}%</TableCell>
                    <TableCell sx={{ color: "#fff" }}>
                      {formatBytes(job.processedBytes)} / {formatBytes(job.totalBytes)}
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>{formatDateTime(job.createdAtUtc)}</TableCell>
                    <TableCell sx={{ color: "#fff" }}>{formatDateTime(job.completedAtUtc)}</TableCell>
                    <TableCell>
                      {canRetry && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReplayIcon />}
                          onClick={async () => {
                            const retried = await retryFolderExportJob(job.jobId);
                            if (!retried) {
                              showError("Unable to retry export job.");
                              return;
                            }

                            upsertJob(retried);
                            showSuccess("Export job moved back to queue.");
                          }}
                          sx={{
                            color: "hsl(19 101% 51% / 1)",
                            borderColor: "hsl(19 101% 51% / 1)",
                            textTransform: "none",
                            mr: 1,
                          }}
                        >
                          Retry
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        disabled={!canDownload}
                        title={canDownload ? EXPORT_DOWNLOAD_HELP_TEXT : undefined}
                        onClick={() => {
                          void (async () => {
                            const started = await triggerExportJobDownloads(job, getFolderExportJob);
                            if (started === 0) {
                              showError("Export completed but no download URL is available.");
                              return;
                            }

                            showInfo(getExportDownloadStartMessage(started));

                            try {
                              await logFolderExportDownload(job.jobId, started);
                            } catch {
                              // Do not block downloads on audit logging failure.
                            }
                          })();
                        }}
                        sx={{
                          color: "hsl(19 101% 51% / 1)",
                          borderColor: "hsl(19 101% 51% / 1)",
                          textTransform: "none",
                        }}
                      >
                        Download
                      </Button>
                      {canShowReason && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => showInfo(getJobReason(job))}
                          sx={{ color: "#fff", textTransform: "none", ml: 1 }}
                        >
                          View reason
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </Box>
        <TablePagination
          component="div"
          count={filteredJobs.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{ color: "#fff", borderTop: "1px solid rgba(255,255,255,0.14)" }}
        />
      </TableContainer>
    </Box>
  );
};

export default DocumentExports;
